import { tv } from "tailwind-variants";

import type { VariantProps } from "tailwind-variants";
import type { SlotsToClasses } from "../../utils/types";

export const segmentedControlVariants = tv({
  slots: {
    // Container for the segmented control group
    group: ["inline-flex w-fit flex-col items-start"],

    // Group label (optional)
    groupLabel: ["mb-1.5 text-sm font-semibold leading-5 text-primary"],

    // Wrapper for the buttons (the "track" background)
    buttonsWrapper: ["flex gap-0.5 rounded-md bg-gray-100 p-0.5 dark:bg-gray-800"],

    // Individual button
    button: [
      "group relative flex cursor-pointer items-center justify-center",
      "rounded-[4px] px-3 py-1.5",
      "text-sm font-medium leading-5 text-secondary",
      "transition-all duration-150 ease-out",
      "outline-none",
      // Hover state (not selected, not disabled)
      "data-[hovered]:text-primary",
      // Selected state
      "data-[selected]:bg-white data-[selected]:text-primary data-[selected]:shadow-sm",
      "dark:data-[selected]:bg-gray-700 dark:data-[selected]:shadow-none",
      // Focus visible state
      "data-[focus-visible]:ring-2 data-[focus-visible]:ring-blue-500 data-[focus-visible]:ring-offset-1",
      "dark:data-[focus-visible]:ring-offset-gray-800",
      // Disabled state
      "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
    ],

    // Button content wrapper
    buttonContent: ["flex items-center gap-1.5"],
  },
  variants: {
    size: {
      sm: {
        button: "px-2 py-1 text-xs",
      },
      md: {
        button: "px-3 py-1.5 text-sm",
      },
      lg: {
        button: "px-4 py-2 text-base",
      },
    },
    fullWidth: {
      true: {
        group: "w-full",
        buttonsWrapper: "w-full",
        button: "flex-1",
      },
    },
    variant: {
      default: {
        buttonsWrapper: "bg-gray-100 dark:bg-gray-800",
        button: "data-[selected]:bg-white dark:data-[selected]:bg-gray-700",
      },
      solid: {
        buttonsWrapper: "bg-gray-100 dark:bg-gray-800",
        button: [
          "data-[selected]:bg-blue-500 data-[selected]:text-white",
          "data-[selected]:data-[hovered]:bg-blue-600",
          "dark:data-[selected]:bg-blue-600",
          "dark:data-[selected]:data-[hovered]:bg-blue-700",
        ],
      },
    },
  },
  defaultVariants: {
    size: "md",
    fullWidth: false,
    variant: "solid",
  },
});

export type SegmentedControlVariantProps = VariantProps<typeof segmentedControlVariants>;
export type SegmentedControlSlots = ReturnType<typeof segmentedControlVariants>;
export type SegmentedControlClassNames = SlotsToClasses<keyof SegmentedControlSlots>;
